import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import { WavePodiumItemContentOutcomes } from "./WavePodiumItemContentOutcomes";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { motion } from "framer-motion";
import { WaveWinnersPodiumPlaceholder } from "./WaveWinnersPodiumPlaceholder";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { WaveWinnerIdentity } from "../identity/WaveWinnerIdentity";
interface WavePodiumItemProps {
  readonly winner?: ApiWaveDecisionWinner | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly position: "first" | "second" | "third";
  readonly customAnimationIndex?: number | undefined;
}

// Configuration for position-specific styling with CSS ready classes for Tailwind
const positionStyles = {
  first: {
    color: "#fbbf24",
    // amber-400 - Gold from WinnerDropBadge
    height: "tw-h-[220px]",
    pfpSize: "tw-size-12 md:tw-size-14",
    marginBottom: "-tw-mb-6",
    textColor: "tw-text-[#fbbf24]",
    ring: "tw-ring-[#fbbf24]",
    shadow: "tw-shadow-[0_0_20px_rgba(251,191,36,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base md:tw-text-xl",
    ratingFontSize: "tw-text-sm sm:tw-text-base md:tw-text-2xl",
    positionText: "1st",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#fbbf24]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#fbbf24]/5",
      via: "tw-via-[#fbbf24]/3",
      hover: "group-hover:desktop-hover:tw-from-[#fbbf24]/[0.07]",
      top: "tw-via-[#fbbf24]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#fbbf24]/25",
      sides: "tw-from-[#fbbf24]/10 tw-via-[#fbbf24]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#fbbf24]/25"
    }
  },
  second: {
    color: "#94a3b8",
    // slate-400 - Silver from WinnerDropBadge
    height: "tw-h-[190px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#94a3b8]",
    ring: "tw-ring-[#94a3b8]",
    shadow: "tw-shadow-[0_0_20px_rgba(148,163,184,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-sm sm:tw-text-base",
    positionText: "2nd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#94a3b8]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#94a3b8]/5",
      via: "tw-via-[#94a3b8]/3",
      hover: "group-hover:desktop-hover:tw-from-[#94a3b8]/[0.07]",
      top: "tw-via-[#94a3b8]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#94a3b8]/25",
      sides: "tw-from-[#94a3b8]/10 tw-via-[#94a3b8]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#94a3b8]/25"
    }
  },
  third: {
    color: "#CD7F32",
    // Bronze (same in both components)
    height: "tw-h-[170px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#CD7F32]",
    ring: "tw-ring-[#CD7F32]",
    shadow: "tw-shadow-[0_0_20px_rgba(205,127,50,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-base",
    positionText: "3rd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#CD7F32]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#CD7F32]/5",
      via: "tw-via-[#CD7F32]/3",
      hover: "group-hover:desktop-hover:tw-from-[#CD7F32]/[0.07]",
      top: "tw-via-[#CD7F32]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#CD7F32]/25",
      sides: "tw-from-[#CD7F32]/10 tw-via-[#CD7F32]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#CD7F32]/25"
    }
  }
};

// Animation variants for the podium items
const podiumVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.2, 0.9, 0.3, 1],
      opacity: {
        duration: 0.25
      }
    }
  })
};

// Height mapping for different positions
const heightMap = {
  first: "tw-h-[220px]",
  second: "tw-h-[190px]",
  third: "tw-h-[170px]"
};
const animationIndexMap: Record<WavePodiumItemProps["position"], number> = {
  first: 0,
  second: 1,
  third: 2
};
const getHoverTextColorClass = (position: WavePodiumItemProps["position"]) => {
  const colorMap = {
    first: "desktop-hover:hover:tw-text-[#fbbf24]",
    second: "desktop-hover:hover:tw-text-[#94a3b8]",
    third: "desktop-hover:hover:tw-text-[#CD7F32]"
  } as const;
  return colorMap[position];
};
export const WavePodiumItem: React.FC<WavePodiumItemProps> = t0 => {
  const $ = _c(104);
  const {
    winner,
    onDropClick,
    position,
    customAnimationIndex
  } = t0;
  const styles = positionStyles[position];
  let t1;
  if ($[0] !== position) {
    t1 = getHoverTextColorClass(position);
    $[0] = position;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hoverTextColorClass = t1;
  if (!winner) {
    const t2 = heightMap[position];
    let t3;
    if ($[2] !== position || $[3] !== t2) {
      t3 = <WaveWinnersPodiumPlaceholder height={t2} position={position} />;
      $[2] = position;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  const drop = winner.drop as ExtendedDrop;
  const animationIndex = customAnimationIndex ?? animationIndexMap[position];
  let t2;
  if ($[5] !== drop || $[6] !== onDropClick) {
    t2 = () => onDropClick(drop);
    $[5] = drop;
    $[6] = onDropClick;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = `tw-flex tw-flex-col tw-items-center ${styles.marginBottom} tw-relative tw-z-10`;
  const t4 = `tw-absolute tw-inset-0 ${styles.bgGradient}`;
  let t5;
  if ($[8] !== t4) {
    t5 = <div className={t4} />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `/${drop.author.handle}`;
  let t7;
  if ($[10] !== drop.author.pfp || $[11] !== styles.pfpSize || $[12] !== styles.ring || $[13] !== styles.shadow) {
    t7 = drop.author.pfp ? <Image src={getScaledImageUri(resolveIpfsUrlSync(drop.author.pfp), ImageScale.W_AUTO_H_50)} alt="" width={56} height={56} className={`${styles.pfpSize} tw-rounded-xl tw-ring-2 ${styles.ring} tw-object-cover ${styles.shadow}`} /> : <div className={`${styles.pfpSize} tw-rounded-xl tw-ring-2 ${styles.ring} ${styles.shadow} tw-bg-iron-900`} />;
    $[10] = drop.author.pfp;
    $[11] = styles.pfpSize;
    $[12] = styles.ring;
    $[13] = styles.shadow;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <Link href={t6} onClick={_temp} className="tw-transform tw-transition-all tw-duration-300 hover:tw-scale-105">{t7}</Link>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = `tw-w-3 md:tw-size-4 ${styles.textColor}`;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t9) {
    t11 = <svg className={t9} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">{t10}</svg>;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = `${styles.textColor} tw-text-xs tw-font-medium lg:tw-text-sm`;
  let t13;
  if ($[21] !== styles.positionText || $[22] !== t12) {
    t13 = <span className={t12}>{styles.positionText}</span>;
    $[21] = styles.positionText;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13) {
    t14 = <div className="tw-absolute tw-inset-x-0 -tw-bottom-3 tw-flex tw-justify-center"><div className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-iron-700 tw-bg-iron-900/80 tw-px-3 tw-py-1 tw-shadow-lg tw-backdrop-blur-sm">{t11}{t13}</div></div>;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t3 || $[29] !== t5 || $[30] !== t8) {
    t15 = <div className={t3}>{t5}{t8}{t14}</div>;
    $[27] = t14;
    $[28] = t3;
    $[29] = t5;
    $[30] = t8;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = `${styles.height} tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-iron-800/60 tw-bg-gradient-to-b tw-from-iron-900/70 tw-via-iron-900/50 tw-to-transparent tw-shadow-[0_4px_24px_rgba(0,0,0,0.2)] tw-backdrop-blur-xl tw-transition-all tw-duration-300 tw-ease-out group-hover:desktop-hover:tw-border-iron-700/70 group-hover:desktop-hover:tw-from-iron-900/75 group-hover:desktop-hover:tw-via-iron-900/55 group-hover:desktop-hover:tw-to-transparent group-hover:desktop-hover:tw-shadow-[0_0_48px_rgba(0,0,0,0.35)]`;
  const t17 = `tw-absolute tw-inset-0 tw-bg-gradient-to-b ${styles.gradient.from} ${styles.gradient.via} tw-to-transparent ${styles.gradient.hover}`;
  let t18;
  if ($[32] !== t17) {
    t18 = <div className={t17} />;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = `tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent ${styles.gradient.top} tw-to-transparent ${styles.gradient.hover25}`;
  let t20;
  if ($[34] !== t19) {
    t20 = <div className={t19} />;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = `tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
  let t22;
  if ($[36] !== t21) {
    t22 = <div className={t21} />;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
  let t24;
  if ($[38] !== t23) {
    t24 = <div className={t23} />;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-3/4 tw-bg-gradient-to-t tw-from-black/20 tw-via-black/10 tw-to-transparent" />;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t18 || $[42] !== t20 || $[43] !== t22 || $[44] !== t24) {
    t26 = <div className="tw-absolute tw-inset-0">{t18}{t20}{t22}{t24}{t25}</div>;
    $[41] = t18;
    $[42] = t20;
    $[43] = t22;
    $[44] = t24;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const t27 = drop.author.handle ?? drop.author.id;
  const t28 = `/${drop.author.handle}`;
  const t29 = `tw-relative tw-mb-2 tw-mt-2 tw-text-center tw-no-underline tw-transition-all sm:tw-mt-4 ${hoverTextColorClass} tw-group/link`;
  const t30 = `${styles.autorFontSize} tw-font-semibold tw-text-iron-200 ${hoverTextColorClass} tw-inline-flex tw-items-center tw-transition-colors`;
  const t31 = `tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-absolute tw-left-[100%] tw-ml-2 desktop-hover:group-hover/link:tw-opacity-100`;
  let t32;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] !== t31) {
    t33 = <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={t31}>{t32}</svg>;
    $[47] = t31;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== drop.author.handle || $[50] !== t30 || $[51] !== t33) {
    t34 = <span className={t30}>{drop.author.handle}{t33}</span>;
    $[49] = drop.author.handle;
    $[50] = t30;
    $[51] = t33;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] !== t28 || $[54] !== t29 || $[55] !== t34) {
    t35 = <Link href={t28} onClick={_temp2} className={t29}>{t34}</Link>;
    $[53] = t28;
    $[54] = t29;
    $[55] = t34;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] !== t27 || $[58] !== t35) {
    t36 = <UserProfileTooltipWrapper user={t27}>{t35}</UserProfileTooltipWrapper>;
    $[57] = t27;
    $[58] = t35;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== drop) {
    t37 = <WaveWinnerIdentity drop={drop} variant="compact" className="tw-mb-2 tw-w-full tw-px-3 sm:tw-px-4" />;
    $[60] = drop;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  const t38 = `${drop.rating >= 0 ? styles.textColor : "tw-text-[#ff4466]"} tw-font-semibold ${styles.ratingFontSize}`;
  let t39;
  if ($[62] !== drop.rating) {
    t39 = formatNumberWithCommas(drop.rating);
    $[62] = drop.rating;
    $[63] = t39;
  } else {
    t39 = $[63];
  }
  let t40;
  if ($[64] !== t38 || $[65] !== t39) {
    t40 = <span className={t38}>{t39}</span>;
    $[64] = t38;
    $[65] = t39;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  const t41 = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  let t42;
  if ($[67] !== t41) {
    t42 = <span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{t41}</span>;
    $[67] = t41;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== t40 || $[70] !== t42) {
    t43 = <div className="tw-flex tw-items-center tw-gap-x-1">{t40}{t42}</div>;
    $[69] = t40;
    $[70] = t42;
    $[71] = t43;
  } else {
    t43 = $[71];
  }
  let t44;
  if ($[72] !== drop.raters_count) {
    t44 = formatNumberWithCommas(drop.raters_count);
    $[72] = drop.raters_count;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== t44) {
    t45 = <span className="tw-text-xs tw-text-iron-200 sm:tw-text-sm">{t44}</span>;
    $[74] = t44;
    $[75] = t45;
  } else {
    t45 = $[75];
  }
  const t46 = drop.raters_count === 1 ? "voter" : "voters";
  let t47;
  if ($[76] !== t46) {
    t47 = <span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{t46}</span>;
    $[76] = t46;
    $[77] = t47;
  } else {
    t47 = $[77];
  }
  let t48;
  if ($[78] !== t45 || $[79] !== t47) {
    t48 = <div className="tw-flex tw-items-center tw-gap-x-1">{t45}{t47}</div>;
    $[78] = t45;
    $[79] = t47;
    $[80] = t48;
  } else {
    t48 = $[80];
  }
  let t49;
  if ($[81] !== winner) {
    t49 = <div onClick={_temp3}><WavePodiumItemContentOutcomes winner={winner} /></div>;
    $[81] = winner;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  let t50;
  if ($[83] !== t48 || $[84] !== t49) {
    t50 = <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t48}{t49}</div>;
    $[83] = t48;
    $[84] = t49;
    $[85] = t50;
  } else {
    t50 = $[85];
  }
  let t51;
  if ($[86] !== t43 || $[87] !== t50) {
    t51 = <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t43}{t50}</div>;
    $[86] = t43;
    $[87] = t50;
    $[88] = t51;
  } else {
    t51 = $[88];
  }
  let t52;
  if ($[89] !== t16 || $[90] !== t26 || $[91] !== t36 || $[92] !== t37 || $[93] !== t51) {
    t52 = <div className="tw-relative tw-w-full"><div className={t16}>{t26}{t36}{t37}{t51}</div></div>;
    $[89] = t16;
    $[90] = t26;
    $[91] = t36;
    $[92] = t37;
    $[93] = t51;
    $[94] = t52;
  } else {
    t52 = $[94];
  }
  let t53;
  if ($[95] !== t15 || $[96] !== t52) {
    t53 = <div className="tw-flex tw-flex-col tw-items-center">{t15}{t52}</div>;
    $[95] = t15;
    $[96] = t52;
    $[97] = t53;
  } else {
    t53 = $[97];
  }
  let t54;
  if ($[98] !== t2 || $[99] !== t53) {
    t54 = <div onClick={t2} className="tw-group tw-cursor-pointer">{t53}</div>;
    $[98] = t2;
    $[99] = t53;
    $[100] = t54;
  } else {
    t54 = $[100];
  }
  let t55;
  if ($[101] !== animationIndex || $[102] !== t54) {
    t55 = <motion.div variants={podiumVariants} initial="hidden" animate="visible" custom={animationIndex}>{t54}</motion.div>;
    $[101] = animationIndex;
    $[102] = t54;
    $[103] = t55;
  } else {
    t55 = $[103];
  }
  return t55;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}